import fs from 'fs';
import path from 'path';
import settings from '@/settings';
import BackToTopButton from '@/components/collections/BackToTopButton';
import { getShortenCountryName } from '@/lib/use-origin-country';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import MarkdownBlock from '@/components/v2/common/MarkdownBlock';

export async function getStaticProps() {
  const baseDir = path.join(process.cwd(), 'content/policies');

  const defaultPath = path.join(baseDir, 'cookie-policy.md');
  const storeSpecificPath = path.join(
    baseDir,
    `cookie-policy-${settings.store}.md`,
  );

  let content: string;

  if (fs.existsSync(storeSpecificPath)) {
    content = fs.readFileSync(storeSpecificPath, 'utf8');
  } else if (fs.existsSync(defaultPath)) {
    content = fs.readFileSync(defaultPath, 'utf8');
  } else {
    throw new Error(
      `Missing cookie policy markdown for store "${settings.store}"`,
    );
  }

  return { props: { content } };
}

export default function CookiePolicy({ content }: { content: string }) {
  if (!content) {
    return null;
  }

  const country = getShortenCountryName();
  const localizedMeta: Partial<
    Record<typeof settings.store, { title: string; description: string }>
  > = {
    'reebelo-fr': {
      title: `Politique en matière de cookies - Reebelo ${country}`,
      description: `Découvrez comment Reebelo ${settings.country} utilise les cookies et technologies similaires, et comment gérer vos préférences.`,
    },
    'reebelo-de': {
      title: `Cookie-Richtlinie - Reebelo ${country}`,
      description: `Erfahren Sie, wie Reebelo ${settings.country} Cookies und ähnliche Technologien verwendet und wie Sie Ihre Präferenzen verwalten können.`,
    },
    'reebelo-at': {
      title: `Cookie-Richtlinie - Reebelo ${country}`,
      description: `Erfahren Sie, wie Reebelo ${settings.country} Cookies und ähnliche Technologien verwendet und wie Sie Ihre Präferenzen verwalten können.`,
    },
    'reebelo-es': {
      title: `Política de Cookies - Reebelo ${country}`,
      description: `Descubre cómo Reebelo ${settings.country} utiliza las cookies y tecnologías similares, y cómo gestionar tus preferencias.`,
    },
    'reebelo-it': {
      title: `Informativa sui Cookie - Reebelo ${country}`,
      description: `Scopri come Reebelo ${settings.country} utilizza i cookie e le tecnologie simili e come gestire le tue preferenze.`,
    },
    'reebelo-se': {
      title: `Cookiepolicy - Reebelo ${country}`,
      description: `Läs hur Reebelo ${country} använder cookies och liknande tekniker, och hur du hanterar dina inställningar.`,
    },
  };
  const meta = localizedMeta[settings.store] ?? {
    title: `Cookie Policy - Reebelo ${country}`,
    description: `Reebelo ${settings.country} is APAC's leading marketplace for sustainable tech with offices in ${settings.country_office_locations}. The positive customer experience at Reebelo ${settings.country} is of utmost importance to us. Please check here for Reebelo ${settings.country} Cookie Policy.`,
  };

  return (
    <>
      <WebsiteSchema title={meta.title} description={meta.description} />
      <BackToTopButton />
      <div
        className="reebelo-container max-w-[800px] py-8"
        data-testid="cookie-policy-page"
      >
        <MarkdownBlock content={content} />
      </div>
    </>
  );
}
